import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  ShiftNotesQuota,
  ShiftNotesQuotaSerializedEntity,
  ShiftNotesQuotasState,
} from './types';

export const shiftNoteQuotasAdapter = createEntityAdapter<ShiftNotesQuota>();

export const initialState: ShiftNotesQuotasState = {
  ...shiftNoteQuotasAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    upsertMany: shiftNoteQuotasAdapter.upsertMany,
    removeAll: shiftNoteQuotasAdapter.removeAll,
    updateShiftNotesQuotas: (state, action) => {
      // the types are inconsistent,
      //  it's shiftNotesQuota here not shiftNotesQuota(s)
      if (action.payload?.data?.type === 'shiftNotesQuota') {
        try {
          const shiftNotesQuota = action.payload?.data;
          shiftNoteQuotasAdapter.upsertOne(state, {
            id: shiftNotesQuota.id,
            type: shiftNotesQuota.type,
            unlimited: shiftNotesQuota.attributes.unlimited,
            used: shiftNotesQuota.attributes.used,
            allowed: shiftNotesQuota.attributes.allowed,
            month: shiftNotesQuota.attributes.month,
            year: shiftNotesQuota.attributes.year,
          });
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }

      if (action.payload?.included) {
        try {
          const shiftNotesQuota = action.payload?.included.find(
            (entity: {
              type: string;
            }): entity is ShiftNotesQuotaSerializedEntity =>
              entity?.type === 'shiftNotesQuota'
          );

          if (!shiftNotesQuota) {
            return;
          }

          shiftNoteQuotasAdapter.upsertOne(state, {
            id: shiftNotesQuota.id,
            type: shiftNotesQuota.type,
            unlimited: shiftNotesQuota.attributes.unlimited,
            used: shiftNotesQuota.attributes.used,
            allowed: shiftNotesQuota.attributes.allowed,
            month: shiftNotesQuota.attributes.month,
            year: shiftNotesQuota.attributes.year,
          });
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    },
  },
  extraReducers: builder => {
    builder.addCase('resources/fetch/pending', (state, action: any) => {
      if (action.meta?.arg?.name === 'schedule') {
        state.status = 'pending';
      }
    });
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state, action: ScheduleFetchAction<ShiftNotesQuotaSerializedEntity>) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const shiftNotesQuotas: ShiftNotesQuota[] = action.payload.included
              .filter(
                (entity): entity is ShiftNotesQuotaSerializedEntity =>
                  entity.type === 'shiftNotesQuota'
              )
              .map(entity => ({
                id: entity.id,
                type: entity.type,
                unlimited: entity.attributes.unlimited,
                used: entity.attributes.used,
                allowed: entity.attributes.allowed,
                month: entity.attributes.month,
                year: entity.attributes.year,
              }));
            shiftNoteQuotasAdapter.setAll(state, shiftNotesQuotas);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectShiftNotesQuotaById,
  selectIds: selectShiftNotesQuotaIds,
  selectEntities: selectShiftNotesQuotaEntities,
  selectAll: selectAllShiftNotesQuotas,
  selectTotal: selectTotalShiftNotesQuotas,
} = shiftNoteQuotasAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { reducer, actions } = slice;

export const selectShiftNotesQuotasStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectShiftNotesQuotasFetchIsPending = (
  state: RootState
): boolean => selectShiftNotesQuotasStatus(state) === 'pending';

export const selectShiftNotesQuotaAttribute = <
  T extends keyof ShiftNotesQuota
>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectShiftNotesQuotaById,
    id,
  });
